import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

/**
 * Adds plausibility behind the aggressive 53–63% bundle discount.
 * Renders a small launch-promo notice with a scarcity counter that
 * decrements throughout the day, so the deep discount feels like a
 * timed launch promo (not a permanent suspicious markdown).
 */

const TIERS = {
  2: { startStock: 500, regularPrice: 279, savePer: 92 },
  4: { startStock: 250, regularPrice: 479, savePer: 182 },
};

const TIER_KEY = "dp_promo_stock_v1";

function getDailyStockLeft(qty) {
  const tier = TIERS[qty];
  if (!tier) return null;

  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const stored = JSON.parse(localStorage.getItem(TIER_KEY) || "{}");
  const key = `${today}_${qty}`;

  if (stored[key]) return stored[key];

  // Calculate elapsed-day-fraction (00:00 → 23:59 maps 0 → 1)
  const now = new Date();
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const elapsed = (now - dayStart) / (24 * 60 * 60 * 1000);

  // Stock decays from startStock down to ~12% by midnight
  const left = Math.max(
    Math.floor(tier.startStock * 0.12),
    Math.floor(tier.startStock * (1 - elapsed * 0.88))
  );

  stored[key] = left;
  localStorage.setItem(TIER_KEY, JSON.stringify(stored));
  return left;
}

export default function LaunchPromoExplainer({ qty }) {
  const tier = TIERS[qty];
  const [left, setLeft] = useState(() => (tier ? getDailyStockLeft(qty) : null));

  useEffect(() => {
    if (!tier) return;
    setLeft(getDailyStockLeft(qty));
    // Decrement randomly every 8-25s for a "live" feel
    const t = setInterval(() => {
      setLeft((curr) => {
        if (curr === null) return curr;
        const dec = Math.random() < 0.45 ? 1 : 0;
        const next = Math.max(Math.floor(tier.startStock * 0.12), curr - dec);
        const today = new Date().toISOString().slice(0, 10);
        const stored = JSON.parse(localStorage.getItem(TIER_KEY) || "{}");
        stored[`${today}_${qty}`] = next;
        localStorage.setItem(TIER_KEY, JSON.stringify(stored));
        return next;
      });
    }, 12000);
    return () => clearInterval(t);
  }, [qty, tier]);

  if (!tier) return null;

  const pct = Math.round((left / tier.startStock) * 100);
  const isUrgent = pct <= 25;

  return (
    <div
      data-testid={`launch-promo-explainer-${qty}`}
      className={`mt-4 rounded-2xl border px-4 py-3 transition ${
        isUrgent
          ? "border-[#ef4444] bg-gradient-to-r from-[#ef4444]/15 to-white"
          : "border-[#d8f06b] bg-gradient-to-r from-[#d8f06b]/25 to-white"
      }`}
    >
      <div className="flex items-start gap-3">
        {isUrgent ? (
          <span className="text-[16px] leading-none mt-0.5 shrink-0 pulse-dot inline-block">🔥</span>
        ) : (
          <Sparkles size={16} className="text-[#0e0e10] mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div
            className={`text-[11px] uppercase tracking-[0.18em] font-extrabold ${
              isUrgent ? "text-[#ef4444]" : "text-[#0e0e10]"
            }`}
          >
            {isUrgent
              ? `Going fast · last ${left} bundles`
              : "Launch promo · Why so cheap?"}
          </div>
          <p className="mt-1 text-[12px] text-[#3a3a40] leading-snug">
            We're launching globally. The first{" "}
            <strong className="text-[#0e0e10]">{tier.startStock} bundles</strong> of{" "}
            {qty} pillows ship at this price. After that we move back to{" "}
            <strong className="text-[#0e0e10]">${tier.regularPrice}</strong>. No
            tricks, no expiring trials — just our launch push.
          </p>

          <div className="mt-2.5 flex items-center justify-between text-[11px] font-bold">
            <span
              className={`tabular-nums ${
                isUrgent ? "text-[#ef4444] pulse-dot" : "text-[#0e0e10]"
              }`}
            >
              <span className={isUrgent ? "text-[#ef4444]" : "text-[#ef4444]"}>
                {left}
              </span>{" "}
              / {tier.startStock} left today
            </span>
            <span className="text-[#22c55e]">
              Save ${tier.savePer} vs regular price
            </span>
          </div>
          <div
            className={`mt-1.5 h-1.5 w-full rounded-full overflow-hidden border ${
              isUrgent ? "bg-white border-[#ef4444]/30" : "bg-white border-[#0e0e10]/10"
            }`}
          >
            <div
              className={`h-full transition-[width] duration-700 ${
                isUrgent
                  ? "bg-[#ef4444]"
                  : "bg-gradient-to-r from-[#d8f06b] to-[#ef4444]"
              }`}
              style={{ width: `${Math.min(100, 100 - pct)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
